import { useLayoutEffect, useState, type MouseEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { auth } from '@/core/auth/authService';
import { env } from '@/core/env';
import { notifier, notifyError } from '@/core/notify/notify';
import { matrixPath } from '@/core/routing/matrix';
import { menuService } from '@/core/services/menu';
import { subscriptionService, type SubscriptionAddon } from '@/core/services/subscription';
import { workspaceService, type WorkspaceNotification, type WorkspaceNotifications } from '@/core/services/workspace';
import { selectClient, useSelectedClient } from '@/core/selectedClient';
import { computeColors, initialThemeColor, saveThemeColor, THEME_COLORS } from '@/core/theme/theme';
import { shortName } from '@/core/utils/shortName';
import { Icon } from '@/kit/Icon';
import { Menu, useMenu } from '@/kit/Menu';
import { isLinkActive } from '@/app/layouts/AsideMenu';
import { ClientSwitcher } from '@/app/layouts/ClientSwitcher';
import { toggleSidebar } from '@/core/sidebar';
import './header.scss';

/** Anchor click handler that opens a menu (the anchor is the popover's anchor) without following the '#' href. */
const opens =
  (open: (event: MouseEvent<HTMLElement>) => void, extra?: () => void) => (event: MouseEvent<HTMLElement>) => {
    event.preventDefault();
    open(event);
    extra?.();
  };

/** Angular's `href="javascript:void(0)"` links: React blocks javascript: URLs, so use a no-op href. */
const noNavigate = (action: () => void) => (event: MouseEvent) => {
  event.preventDefault();
  action();
};

/** `<a mat-menu-item class="d-flex align-items-center gap-5">`: Material's item DOM (icon, item-text span, ripple). */
function QuickAddItem({
  icon,
  children,
  ...rest
}: { icon: string; children: string } & (
  | { to: string; href?: undefined; onClick?: undefined }
  | { to?: undefined; href: string; onClick: (event: MouseEvent) => void }
)) {
  const props = {
    className: 'mat-mdc-menu-item mat-mdc-focus-indicator d-flex align-items-center gap-5',
    role: 'menuitem',
    tabIndex: 0,
    'aria-disabled': false,
  };
  const content = (
    <>
      <Icon name={icon} outlined className="add-icon" />
      <span className="mat-mdc-menu-item-text">{children}</span>
      <div className="mat-ripple mat-mdc-menu-ripple"></div>
    </>
  );
  return rest.to !== undefined ? (
    <Link {...props} to={rest.to}>
      {content}
    </Link>
  ) : (
    <a {...props} href={rest.href} onClick={rest.onClick}>
      {content}
    </a>
  );
}

export function Header() {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  // ---- profile and subscription (Angular dispatched GetProfileInfo / RetrieveSubscription on init) ----
  const profile = useQuery({ queryKey: ['header', 'profile'], queryFn: menuService.getProfileInfo, gcTime: 0 });
  const subscription = useQuery({
    queryKey: ['header', 'subscription'],
    queryFn: subscriptionService.retrieveSubscription,
    gcTime: 0,
  });

  // The header shows `imagePath` as-is: only an explicit null falls back to initials; '' before load, undefined if absent.
  const userPhoto = profile.data ? profile.data.imagePath : '';
  const userName = profile.data?.name ?? '';
  const userId = profile.data?.id;
  const userEmail = profile.data?.email;

  const addons: SubscriptionAddon[] = subscription.data?.isSuccess ? (subscription.data.data?.listofAddons ?? []) : [];
  const myAdminUrl = [...addons].reverse().find((a) => a.externalName === 'My Admin')?.appUrl ?? '';

  // ---- theme ----
  const [colorSelected, setColorSelected] = useState<string>(() => initialThemeColor());
  useLayoutEffect(() => {
    computeColors(initialThemeColor());
  }, []);

  const onSelectionChange = (color: string) => {
    setColorSelected(color);
    computeColors(color);
    saveThemeColor(color).catch(() => undefined);
  };

  // ---- notifications ----
  const [notifications, setNotifications] = useState<WorkspaceNotification[]>([]);
  const [notificationSummary, setNotificationSummary] = useState('');
  const [unreadNotifications, setUnreadNotifications] = useState(0);
  const [isLoadingNotifications, setIsLoadingNotifications] = useState(false);

  const applyNotifications = (result: WorkspaceNotifications) => {
    setNotifications(result.items ?? []);
    setNotificationSummary(result.summary);
    setUnreadNotifications(result.unreadCount ?? 0);
  };

  /** Loads what needs attention across the firm: overdue accounts and unbalanced trial balances. */
  const onAnnouncementClick = () => {
    if (isLoadingNotifications) return;
    setIsLoadingNotifications(true);
    workspaceService
      .getNotifications()
      .then((result) => {
        setIsLoadingNotifications(false);
        applyNotifications(result);
      })
      .catch((error) => {
        setIsLoadingNotifications(false);
        notifyError(error);
      });
  };

  const refresh = (call: Promise<WorkspaceNotifications>) => call.then(applyNotifications).catch(notifyError);

  /** Opens the client the notification is about, marking it read for this user. */
  const openNotification = (item: WorkspaceNotification) => {
    if (!item.isRead) {
      void refresh(workspaceService.markNotifications([item.key], true));
    }
    if (item.clientId && item.clientName) {
      selectClient(item.clientId, item.clientName);
    }
    if (item.link) {
      void navigate(item.link);
    }
  };

  const dismissNotification = (item: WorkspaceNotification, event: MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    void refresh(workspaceService.dismissNotifications([item.key], true));
  };

  const readNotificationCount = notifications.filter((n) => n.isRead).length;

  const dismissReadNotifications = () => {
    const keys = notifications.filter((n) => n.isRead).map((n) => n.key);
    if (keys.length === 0) return;
    void refresh(workspaceService.dismissNotifications(keys, true));
  };

  const markAllNotificationsRead = () => {
    const keys = notifications.filter((n) => !n.isRead).map((n) => n.key);
    if (keys.length === 0) return;
    void refresh(workspaceService.markNotifications(keys, true));
  };

  // ---- quick add ----
  // Reactive (APT244): the bar in the header must rename itself the moment this switcher - or any other client picker -
  // changes the client, not just on the next navigation.
  const client = useSelectedClient();
  /**
   * APT236: the client actions (accounting period, trial balance, journal, set of accounts) are offered only inside a
   * client - its pages are under /home/taxreturns - not on the dashboard or the Accounts and Manage screens, where the
   * client last opened is still remembered.
   */
  const insideClient = client !== null && pathname.startsWith('/home/taxreturns');

  const openClientAction = (url: string) => void navigate(url);

  // ---- menus ----
  const quickAdd = useMenu();
  const modules = useMenu();
  const notificationMenu = useMenu();
  const profileMenu = useMenu();
  const ticketMenu = useMenu();

  const goToProfile = () => {
    if (userId) {
      void navigate(matrixPath('/home/manage', 'addUser', { id: btoa(String(userId)) }));
    }
  };

  /** Products and subscriptions are managed in My Admin, not in accounts production. */
  const onProductsClick = () => {
    if (myAdminUrl) {
      window.open(myAdminUrl, '_blank');
      return;
    }
    notifier.info(
      'Products & Subscriptions',
      'Products and subscriptions are managed in My Admin. Open My Admin from the apps menu to change your subscription.',
    );
  };

  const redirectToModule = (addon: SubscriptionAddon, event: MouseEvent) => {
    if (addon.isDisabled) {
      event.preventDefault();
    }
  };

  const navLink = (to: string) => (isLinkActive(pathname, to) ? 'active' : undefined);

  return (
    <div className="header">
      <div className="d-flex align-items-center justify-space-between wp-100">
        <div className="d-flex align-items-center">
          <button
            type="button"
            className="header-menu-toggle-btn"
            aria-label="Toggle navigation"
            title="Toggle navigation"
            onClick={toggleSidebar}
          >
            <Icon name="menu" />
          </button>
          <div className="logo-container">
            <Link to="/home/dashboard">
              <img className="report-landing-hero-image" alt="Logo" src="assets/images/logo.png" />
            </Link>
          </div>
          <div className="nav-container">
            <div className="navigation-menu">
              <Link className={navLink('/home/dashboard')} to="/home/dashboard">
                <Icon name="space_dashboard" outlined />
                <span>Dashboard</span>
              </Link>
              <Link className={navLink('/home/accountclient')} to="/home/accountclient">
                <Icon name="assignment" outlined />
                <span>Accounts</span>
              </Link>
              <Link className={navLink('/home/manage')} to="/home/manage">
                <Icon name="manage_accounts" outlined />
                <span>Manage</span>
              </Link>
            </div>
          </div>
        </div>

        <div className="account-container">
          <div className="account-menu">
            <ul className="d-flex align-items-center">
              {insideClient && client && (
                <li className="header-secondary header-client">
                  <Icon name="business" outlined />
                  <ClientSwitcher client={client} />
                </li>
              )}
              <li className="header-secondary">
                <a>
                  <Icon
                    name="add_circle_outline"
                    outlined
                    className="mat-settings-icon"
                    title="Quick Add"
                    onClick={quickAdd.onOpen}
                  />
                </a>
                <Menu {...quickAdd} className="action-menu top-menu-list" xPosition="before">
                  <div className="main-menu-list">
                    <div className="menu-list-item">
                      <QuickAddItem to="/home/manage/addClient" icon="person_add">
                        New client
                      </QuickAddItem>
                      <QuickAddItem to="/home/manage/addUser" icon="group_add">
                        New user
                      </QuickAddItem>
                      <QuickAddItem to="/home/ticket" icon="confirmation_number">
                        New support ticket
                      </QuickAddItem>

                      {insideClient && (
                        <>
                          <div className="quick-add-client" onClick={(event) => event.stopPropagation()}>
                            <span className="fw-bold font-size-14">For {client.name}</span>
                          </div>

                          {[
                            ['/home/taxreturns/tasks-accountingperiods', 'event_available', 'New accounting period'],
                            ['/home/taxreturns/tasks-createtrialbalance', 'balance', 'New trial balance'],
                            ['/home/taxreturns/tasks-journals', 'library_add', 'New journal'],
                            ['/home/taxreturns/addnewreport', 'description', 'New set of accounts'],
                          ].map(([url, icon, label]) => (
                            <QuickAddItem
                              key={url}
                              href="#"
                              icon={icon}
                              onClick={noNavigate(() => openClientAction(url))}
                            >
                              {label}
                            </QuickAddItem>
                          ))}
                        </>
                      )}
                    </div>
                  </div>
                </Menu>
              </li>

              <li className="header-secondary">
                <a
                  href="#"
                  className="profile-setting-icon p-0"
                  title="Apply for presenter ID"
                  onClick={noNavigate(() => window.open(env.applyPresenterUrl, '_blank'))}
                >
                  <Icon name="open_in_new" />
                </a>
              </li>

              {/* APT365: the Standard / Classic switch is gone - Classic drew exactly what Standard draws, so clicking it changed nothing. */}
              <li className="header-secondary">
                <Link className="profile-setting-icon" title="General Settings" to="/home/settings">
                  <Icon name="settings_outline" />
                </Link>
              </li>

              <li className="header-secondary module-list separation pl-1">
                <a href="#" title="Modules" onClick={opens(modules.onOpen)}>
                  <div className="m-0">
                    <img
                      className="profile-image-thumb"
                      src="assets/images/company-logo.svg"
                      width="22"
                      height="22"
                      alt="Capium Logo"
                    />
                  </div>
                </a>
                <Menu {...modules} className="action-menu dropdown-module-list" xPosition="after">
                  <div>
                    <ul>
                      {addons
                        .filter((addon) => addon.isDisplay)
                        .map((addon) => (
                          <li className={addon.isDisabled ? 'disable-box' : ''} key={addon.externalName}>
                            <a
                              href={addon.isDisabled || !addon.appUrl ? '#' : addon.appUrl}
                              onClick={(event) => redirectToModule(addon, event)}
                            >
                              <div className="top-card d-flex gap-10">
                                <img src={addon.iconSrc} alt="logo" />
                                <div>
                                  <h6>{addon.externalName}</h6>
                                  <p>{addon.content}</p>
                                </div>
                              </div>
                            </a>
                          </li>
                        ))}
                    </ul>
                    <div className="top-switch-button text-align-right">
                      <button
                        type="button"
                        className="action-button primary-button"
                        onClick={() => void navigate('/home/dashboard')}
                      >
                        <img src="assets/images/capihome.svg" className="pr-5" alt="Icon" />
                        <span>Home </span>
                      </button>
                      <button
                        type="button"
                        className="action-button secondary-button ml-1"
                        onClick={() => window.location.replace(myAdminUrl)}
                      >
                        <img src="assets/images/myadmin.svg" className="pr-5" alt="Icon" />
                        <span>My Admin</span>
                      </button>
                    </div>
                  </div>
                </Menu>
              </li>

              <li>
                <a href="#" title="Notifications" onClick={opens(notificationMenu.onOpen, onAnnouncementClick)}>
                  <Icon name="campaign" outlined />
                  {unreadNotifications > 0 && (
                    <span className="text-danger-100 fw-bold font-size-14"> {unreadNotifications} </span>
                  )}
                </a>
                <Menu {...notificationMenu} className="action-menu notification-menu" xPosition="before">
                  <div className="p-1" onClick={(event) => event.stopPropagation()}>
                    <div className="d-flex align-items-center justify-space-between gap-15">
                      <span className="fw-bold">Notifications</span>
                      <span className="d-flex align-items-center gap-15">
                        {unreadNotifications > 0 && (
                          <a href="#" className="link text-underline" onClick={noNavigate(markAllNotificationsRead)}>
                            Mark all as read
                          </a>
                        )}
                        {readNotificationCount > 0 && (
                          <a href="#" className="link text-underline" onClick={noNavigate(dismissReadNotifications)}>
                            Clear read
                          </a>
                        )}
                      </span>
                    </div>
                    <p className="font-size-14 m-0 pt-5">
                      {isLoadingNotifications ? 'Loading...' : notificationSummary}
                    </p>
                  </div>
                  <div className="notification-list" onClick={(event) => event.stopPropagation()}>
                    {notifications.map((item) => (
                      <div className={`notification-item${item.isRead ? '' : ' is-unread'}`} key={item.key}>
                        <Icon name={item.isRead ? 'drafts' : 'markunread'} outlined className="notification-icon" />
                        <span
                          className="notification-text"
                          title="Open this client"
                          onClick={() => {
                            notificationMenu.onClose();
                            openNotification(item);
                          }}
                        >
                          {item.message}
                        </span>
                        <a
                          href="#"
                          className="notification-dismiss"
                          title="Remove this notification"
                          onClick={(event) => dismissNotification(item, event)}
                        >
                          <Icon name="close" outlined />
                        </a>
                      </div>
                    ))}
                  </div>
                  {!isLoadingNotifications && notifications.length === 0 && (
                    <div className="p-1">Nothing needs your attention.</div>
                  )}
                </Menu>
              </li>

              <li className="header-secondary">
                <a
                  href="#"
                  className={navLink('/home/application-testing')}
                  title="Application Testing"
                  onClick={opens(ticketMenu.onOpen)}
                >
                  <Icon name="bug_report" outlined />
                </a>
                {/* Design review 2 Nov: one Application Testing entry (the old "View Tickets" support-ticket entry is gone from this menu;
                    the page is still reached from the + menu) with the three places the Time & Fees header offers. */}
                <Menu {...ticketMenu} className="action-menu top-menu-list" xPosition="before">
                  <div className="main-menu-list">
                    <div className="menu-list-item">
                      <div className="quick-add-client" onClick={(event) => event.stopPropagation()}>
                        <span className="fw-bold font-size-14">Application Testing</span>
                      </div>
                      <QuickAddItem to="/home/application-testing/dashboard" icon="space_dashboard">
                        Dashboard
                      </QuickAddItem>
                      <QuickAddItem to="/home/application-testing" icon="list_alt">
                        All Tickets
                      </QuickAddItem>
                      <QuickAddItem to="/home/application-testing?new=1" icon="add">
                        New ticket
                      </QuickAddItem>
                    </div>
                  </div>
                </Menu>
              </li>

              <li className="header-secondary">
                <a
                  href="#"
                  className="profile-setting-icon p-0"
                  title="Help"
                  onClick={noNavigate(() => window.open(env.helpDeskUrl, '_blank'))}
                >
                  <Icon name="help_outline" />
                </a>
              </li>

              <li>
                <a href="#" onClick={opens(profileMenu.onOpen)}>
                  {userPhoto !== null && (
                    <div>
                      <img
                        className="profile-image-thumb"
                        src={userPhoto || undefined}
                        alt="User photo"
                        title="User Image"
                      />
                    </div>
                  )}
                  {userPhoto === null && userName && (
                    <div className="rounded-border m-0" title="User Image">
                      {shortName(userName, 2)}
                    </div>
                  )}
                </a>
                <Menu {...profileMenu} className="action-menu profile-setting-menu" xPosition="after">
                  <div className="text-align-center profile-settings">
                    {userPhoto !== null && (
                      <div className="mb-1">
                        <img className="profile-image" src={userPhoto || undefined} alt="User photo" />
                      </div>
                    )}
                    {userPhoto === null && userName && <div className="rounded-border">{shortName(userName, 2)}</div>}

                    <div>
                      <p className="fw-bold text-transform-uppercase text-gray-100 font-size-16 mb-0 mt-10">
                        {userName}
                      </p>
                    </div>
                    <div>
                      <p className="font-size-14 m-0">User ID: {userId}</p>
                    </div>
                    <div>
                      <p className="font-size-14 mb-1 m-0">{userEmail}</p>
                    </div>
                    <div className="hr-top pt-1">
                      <a href="#" onClick={noNavigate(goToProfile)}>
                        <span className="text-gray-100 font-size-16">My Account</span>
                      </a>
                      <span className="pl-2 pr-2 text-gray-100">|</span>
                      <a href="#" onClick={noNavigate(() => auth.signOut())}>
                        <span className="text-danger-100 font-size-16">Sign Out</span>
                      </a>
                    </div>
                    <div className="pt-10 pb-1">
                      <a
                        href="#"
                        className="link text-underline"
                        title="Products and subscriptions are managed in My Admin"
                        onClick={noNavigate(onProductsClick)}
                      >
                        <span className="text-primary font-size-16">Products &amp; Subcriptions</span>
                      </a>
                    </div>
                    <div className="theme-colors">
                      <div className="mat-mdc-radio-group mb-10 display-inline" role="radiogroup">
                        {THEME_COLORS.map((color) => (
                          <div
                            key={color}
                            className={`mat-mdc-radio-button${colorSelected === color ? ' mat-mdc-radio-checked mat-radio-checked' : ''}`}
                            onClick={() => onSelectionChange(color)}
                          >
                            <div className="mdc-radio">
                              <input
                                type="radio"
                                name="theme-color"
                                value={color}
                                checked={colorSelected === color}
                                readOnly
                              />
                            </div>
                            <label className="mdc-label">
                              <button
                                className="theme-selector"
                                style={{ backgroundColor: color }}
                                type="button"
                                aria-label={color}
                              >
                                <Icon name="check" />
                              </button>
                            </label>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </Menu>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
